import {gsap, MorphSVGPlugin} from "gsap/all";
import $ from "jquery";

gsap.registerPlugin(MorphSVGPlugin);

// Background shape that's always visible and morphs between screens, animating a
// path between named shapes (screen 1 is a rounded rect, the rest use the base
// shape from the svg file).
export class BackgroundMorpher {

  constructor(app, options = {}) {
    this.app = app;

    this.selector = options.selector ?? ".bg-morph-shape";

    // Full close and open duration
    this.duration = options.duration ?? 0.8;
    this.shapeEase = options.ease ?? "power2.inOut";

    // Different eases for the close and reopen halves, so it snaps shut and
    // springs back open.
    this.closeEase = options.closeEase ?? "power2.in";
    this.openEase = options.openEase ?? "power2.out";

    // Hold the curtain closed for X seconds before reopening it
    this.closedHold = options.closedHold ?? 0.25;

    // The line the shape collapses down to when closing (the viewBox is
    // 0 0 1793 1038, so the middle is y = 519).
    const centerY = options.centerY ?? 519;
    // How thin the strip is when fully collapsed (in viewBox pixels).
    const closedHeight = options.closedHeight ?? 6;

    const $el = $(this.selector);

    this.shapes = {
      // blob is the base shape from the svg file
      blob: $el.length ? $el.attr("d") : "",
      // Rounded rectangle (screen 1), centered up and down
      rect: BackgroundMorpher.roundedRectPath(0, 47.5, 1788, 943, 66),
      // A thin full-width strip at the middle line — the closed state the shape
      // passes through between screens.
      closed: BackgroundMorpher.roundedRectPath(
        0, centerY - closedHeight / 2, 1788, closedHeight, closedHeight / 2
      ),
      ...(options.shapes ?? {}),
    };

    this.current = null;
    this.tween = null;
  }

  // Morph straight to a shape. Pass {instant: true} to snap there without
  // animating (used on screen 1 to show the shape on load). The curtain
  // transition uses close() and open() instead.
  to(shapeName, {instant = false} = {}) {
    const shape = this.selectShape(shapeName);
    if (!shape) return;

    if (instant) {
      gsap.set(this.selector, {morphSVG: shape});
      return;
    }

    this.tween = gsap.to(this.selector, {
      morphSVG: shape,
      duration: this.duration,
      ease: this.shapeEase,
    });
  }

  // Curtain close: shrink the shape (and the clip path that hides the screen
  // content) down to the thin middle strip. Calls onComplete once fully closed.
  close(onComplete) {
    this.killTween();
    this.current = "closed";
    this.tween = gsap.to(this.selector, {
      morphSVG: this.shapes.closed,
      duration: this.duration / 2,
      ease: this.closeEase,
      onComplete,
    });
    return this.tween;
  }

  // Curtain open: grow from the middle strip into the target shape. Always runs,
  // even if the shape is the same, so the curtain reopens between two screens
  // that share a shape. Calls onComplete once fully open.
  open(shapeName, onComplete) {
    const shape = this.shapes[shapeName];
    if (!shape) {
      if (typeof onComplete === "function") onComplete();
      return null;
    }
    this.killTween();
    this.current = shapeName;
    this.tween = gsap.to(this.selector, {
      morphSVG: shape,
      opacity: 1,
      duration: this.duration / 2,
      ease: this.openEase,
      delay: this.closedHold, // hold the curtain shut briefly before reopening
      onComplete,
    });
    return this.tween;
  }

  // Fade out: animates opacity to 0 after the curtain is closed.
  fadeOut(onComplete) {
    this.killTween();
    this.current = "closedFade";
    this.tween = gsap.to(this.selector, {
      opacity: 0,
      duration: (this.duration / 2) * 0.5, // Make it twice as fast
      ease: this.openEase,
      onComplete,
    });
    return this.tween;
  }

  killTween() {
    if (this.tween) {
      this.tween.kill();
      this.tween = null;
    }
  }

  // Pick the next shape to morph to (used by to()), and stop the current tween
  // if there is one.
  selectShape(shapeName) {
    const shape = this.shapes[shapeName];
    if (!shape || shapeName === this.current) return null;
    this.current = shapeName;
    this.killTween();
    return shape;
  }

  // Builds a rounded-rectangle path with curved corners that MorphSVG likes.
  static roundedRectPath(x, y, w, h, r) {
    const k = r * 0.5522847498; // handle length for a rounded 90 degree corner
    return [
      `M ${x + r} ${y}`,
      `H ${x + w - r}`,
      `C ${x + w - r + k} ${y} ${x + w} ${y + r - k} ${x + w} ${y + r}`,
      `V ${y + h - r}`,
      `C ${x + w} ${y + h - r + k} ${x + w - r + k} ${y + h} ${x + w - r} ${y + h}`,
      `H ${x + r}`,
      `C ${x + r - k} ${y + h} ${x} ${y + h - r + k} ${x} ${y + h - r}`,
      `V ${y + r}`,
      `C ${x} ${y + r - k} ${x + r - k} ${y} ${x + r} ${y}`,
      `Z`,
    ].join(" ");
  }
}
